import { MouseEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GridCellParams,
  GridColDef,
  gridFilteredDescendantCountLookupSelector,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowModes,
  type GridRowModesModel,
  Toolbar,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { DataGridPro, GridApiPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { Box, Button, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import {
  getCommonTargetAttributes,
  updateTargetAttributes,
} from '@/generated/client/targetattributes/targetattributes';
import { MinimalTarget, TargetAttributeDto, TargetAttributeDtoType } from '@/generated/model';
import TypeBasedEditor from './TypeBasedEditor';
import TypeBasedDisplay from './TypeBasedDisplay';
import { SearchComponent } from '../common/Search';
import './TargetAttributes.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useNotificationStore } from '@/stores/notificationstore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DisplayNoResult from '../common/DisplayNoResult';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ctxUser } from '@/contexts/UserContext';
import { targetAttributesExport } from './TargetAttributesExport';
import { translateKey } from './TargetAttributesCommon';
import { toTargetAttributePayloadValue } from '@/components/targetAttributes/TargetAttributePayloadValue';
import {
  mergeSavedTargetAttributeRows,
  normalizeAttributeValueForComparison,
} from '@/components/targetAttributes/TargetAttributeRowState';

const targetAttributeTags = new Set(['vehicle', 'company', 'other']);

export default function TargetAttributesEditor({
  selectedTargets,
  onEditingChange,
}: {
  selectedTargets: MinimalTarget[] | undefined;
  onEditingChange?: (isEditing: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [originalRows, setOriginalRows] = useState<TargetAttributeDto[]>([]);
  const [rows, setRows] = useState<TargetAttributeDto[]>([]);
  const [changedRowKeys, setChangedRowKeys] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState<string | undefined>();
  const [updatedRowKeys, setUpdatedRowKeys] = useState<Set<string>>(new Set());
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const apiRef = useGridApiRef();
  const userCtx = useContext(ctxUser);

  useEffect(() => {
    const isEditing = isRowInEditMode(rowModesModel);
    if (isEditing) {
      onEditingChange?.(isEditing);
    }
  }, [rowModesModel]);

  {
    /*nyelváltás miatt megnyitott ablak mellett, race condition feloldására */
  }
  async function wait() {
    return new Promise((resolve) => {
      setTimeout(resolve, 200);
    });
  }

  async function loadTargetAttributes() {
    setChangedRowKeys(new Set());
    setUpdatedRowKeys(new Set());
    if (!selectedTargets || selectedTargets.length === 0) {
      setOriginalRows([]);
      setRows([]);
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    await wait();
    const perTargetAttributes = (await getCommonTargetAttributes(selectedTargetIds)).data;
    const newTargetAttributes = perTargetAttributes.attributes.map((row) => ({
      ...row,
      translatedKey: translateKey(row, t),
    }));
    setOriginalRows(newTargetAttributes);
    setRows(newTargetAttributes);
    onEditingChange?.(false);
  }

  function matchesSearch(value: unknown, lower: string): boolean {
    if (value === null) return false;

    if (typeof value === 'object') {
      return Object.values(value).some((v) => matchesSearch(v, lower));
    }

    return String(value).toLowerCase().includes(lower);
  }

  useEffect(() => {
    void loadTargetAttributes();
  }, [selectedTargets?.length]);

  useEffect(() => {
    if (!searchText) {
      setRows(originalRows);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = originalRows.filter((row) => Object.values(row).some((v) => matchesSearch(v, lower)));
    setRows(filtered);
  }, [searchText, originalRows]);

  function processRowUpdate(newRow: TargetAttributeDto) {
    setRows((prev) => prev.map((prevRow) => (prevRow.key === newRow.key ? newRow : prevRow)));

    const original = originalRows.find((row) => row.key === newRow.key);
    const changed = !isEqual(
      normalizeAttributeValueForComparison(newRow.value, newRow.type),
      normalizeAttributeValueForComparison(original?.value, original?.type ?? newRow.type)
    );

    setChangedRowKeys((prev) => {
      const updated = new Set(prev);
      if (changed) {
        updated.add(newRow.key);
      } else {
        updated.delete(newRow.key);
      }
      onEditingChange?.(updated.size > 0);
      return updated;
    });
    return newRow;
  }

  async function modifyAttributes() {
    if (!selectedTargets?.length || !changedRowKeys.size) {
      return;
    }
    const selectedTargetIds = selectedTargets.map((target) => target.id);
    const changedRows = rows.filter((row) => changedRowKeys.has(row.key));
    const changedValues = changedRows.map(toTargetAttributePayloadValue);
    await updateTargetAttributes({ targetIds: selectedTargetIds, values: changedValues });
    showNotification('success', t('targetAttributes.successfulModification'), undefined, 3);
    setOriginalRows((prev) => mergeSavedTargetAttributeRows(prev, changedRows));
    setUpdatedRowKeys(changedRowKeys);
    setChangedRowKeys(new Set());
    onEditingChange?.(false);
  }

  const columns: GridColDef[] = [
    {
      // do not use value field. With that valueGetter overrides the original value
      field: 'valueName',
      headerName: t('targetAttributes.valueHeader'),
      minWidth: 260,
      align: 'right',
      flex: 1,
      editable: true,
      renderEditCell: (params) => <TypeBasedEditor params={params} />,
      renderCell: (params) => <TypeBasedDisplay params={params} />,
      // Needed for show values in TypeBasedDisplay component
      valueGetter: (_, row: TargetAttributeDto) => {
        if (
          !row.value // row.value === undefined || row.value === null
        ) {
          return null;
        }
        const type: TargetAttributeDtoType = row.type;
        switch (type) {
          case TargetAttributeDtoType['@list']: {
            return row.value.translation;
          }
          case TargetAttributeDtoType['@class']: {
            return row.value.brand + ' ' + row.value.type;
          }
          default:
            return row.value;
        }
      },
      valueSetter: (value, row) => {
        if (
          (row.type === TargetAttributeDtoType['@class'] || row.type === TargetAttributeDtoType['@list']) &&
          typeof value === 'string'
        ) {
          return row;
        }
        return { ...row, value };
      },
    },
  ];

  function CustomToolbar() {
    return (
      <Toolbar className={'targetattribute-toolbar'}>
        <Box sx={{ width: '100%', mr: 2 }}>
          <SearchComponent
            searchText={searchText}
            setSearchText={setSearchText}
            showHistory={false}
            placeholder={t('targetAttributes.searchPlaceHolder')}
          />
        </Box>
        <Button
          className="export_button"
          onClick={() => targetAttributesExport({ selectedTargets, userCtx, t, i18n })}
          sx={{ textTransform: 'none' }}
        >
          <FileDownloadIcon fontSize="small" color="primary" />
          <>
            &nbsp;<Typography variant="body2">{t('export')}</Typography>
          </>
        </Button>
      </Toolbar>
    );
  }

  function CustomGridTreeDataGroupingCell(props: GridRenderCellParams) {
    const { id, field, rowNode, formattedValue } = props;
    const apiRef = useGridApiContext<GridApiPro>();
    const filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
    const filteredDescendantCount = filteredDescendantCountLookup[rowNode.id] ?? 0;
    const label = rowNode.type === 'group' ? formattedValue : (props.row as TargetAttributeDto).translatedKey;

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
      if (rowNode.type !== 'group') {
        return;
      }
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      apiRef.current.setCellFocus(id, field);
      event.stopPropagation();
    };

    return (
      <Box
        sx={{
          ml: rowNode.depth * 4,
          width: '100%',
          minWidth: 0,
        }}
      >
        <div onClick={handleClick}>
          {rowNode.type === 'group' ? (
            <div className="attribute-category">
              {rowNode.childrenExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              <div className="attribute-category-text">
                {label} ({filteredDescendantCount})
              </div>
            </div>
          ) : (
            <div className="attribute-category-text">{label}</div>
          )}
        </div>
      </Box>
    );
  }

  const groupingColDef = {
    headerName: t('targetAttributes.keyHeader'),
    minWidth: 320,
    flex: 1.4,
    renderCell: (params: GridRenderCellParams) => <CustomGridTreeDataGroupingCell {...params} />,
    // FIXME ne legyen any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueFormatter: (value: any) => {
      return translateTag(value);
    },
    colSpan: (_: unknown, row: TargetAttributeDto) => {
      if (row.type === undefined) {
        return 2;
      }
      return 1;
    },
  };

  function translateTag(tag: string) {
    if (tag.includes('empty_tag')) {
      return '';
    } else if (targetAttributeTags.has(tag)) {
      return t('targetAttributes.type.' + tag);
    }
    return tag;
  }

  function getRowClass(params: GridRowClassNameParams) {
    const rowKey = params.row.key;
    if (rowKey === undefined) {
      const type = params.id.toString().split('auto-generated-row-null/')[1];
      if (targetAttributeTags.has(type)) {
        return 'type-row type-row-' + type;
      }
      return 'type-row type-row-itrack-param';
    }
    if (changedRowKeys.has(rowKey)) {
      return 'changed-row';
    }
    if (updatedRowKeys.has(rowKey)) {
      return 'updated-row';
    }
    return '';
  }

  function isRowInEditMode(rowModesModel: GridRowModesModel) {
    return Object.values(rowModesModel).some((mode) => mode.mode === GridRowModes.Edit);
  }

  function handleCellClickRowEdit(
    params: GridCellParams,
    rowModesModel: GridRowModesModel,
    setRowModesModel: (model: GridRowModesModel) => void
  ) {
    if (params.field === '__tree_data_group__') {
      return;
    }
    const isEditing = isRowInEditMode(rowModesModel);
    if (isEditing) {
      return;
    }
    setRowModesModel({
      ...rowModesModel,
      [params.id]: { mode: GridRowModes.Edit },
    });
  }

  return (
    <>
      <div className="grid-wrapper">
        <div className="target-attributes-grid">
          <DataGridPro
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnMenu
            columnHeaderHeight={0}
            disableColumnSorting
            disableDensitySelector
            disableColumnFilter
            hideFooter
            disableVirtualization
            pageSizeOptions={[100]}
            getRowId={(attribute) => attribute.key}
            treeData
            getTreeDataPath={(row) => [...row.tag, row.key]}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={-1}
            processRowUpdate={processRowUpdate}
            getRowClassName={getRowClass}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => <DisplayNoResult searchText={searchText} />,
            }}
            showToolbar
            hideFooterRowCount
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onCellClick={function (params) {
              handleCellClickRowEdit(params, rowModesModel, setRowModesModel);
            }}
            sx={{
              '--DataGrid-rowBorderColor': 'transparent',
              '& .MuiDataGrid-columnHeaders': {
                display: 'none',
              },
            }}
          />
        </div>
        {changedRowKeys.size !== 0 && (
          <div className="button-container">
            <Button className="targetattribute-button save-button" onClick={modifyAttributes}>
              <CheckCircleOutlineIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.save')}</span>
            </Button>
            <Button className="targetattribute-button cancel-button" onClick={loadTargetAttributes}>
              <CancelOutlinedIcon className="button-icon" />
              <span className="button-text">{t('targetAttributes.button.cancel')}</span>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
